// src/components/settings/sections/BrandingSection.jsx
import React from "react";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormInput from "../../inputs/FormInput";
import { getAccordionStyles } from "./accordionStyles";

const BrandingSection = ({
    expanded,
    handleAccordionChange,
    isDark,
    formData,
    setFormData,
    uploadMedia,
    logoError,
    setLogoError,
    footerError,
    setFooterError,
    faviconError,
    setFaviconError,
    logoUploadedName,
    setLogoUploadedName,
    footerUploadedName,
    setFooterUploadedName,
    faviconUploadedName,
    setFaviconUploadedName,
    isUploadingLogo,
    setIsUploadingLogo,
    isUploadingFooter,
    setIsUploadingFooter,
    isUploadingFavicon,
    setIsUploadingFavicon,
}) => {
    const { accordionBaseSx, summarySx, detailsSx } = getAccordionStyles(isDark);

    // يدعم أكثر من شكل للـ formData بدون ما يبوّظ القديم
    const logoMediaId =
        formData?.logo?.id ?? formData?.logo_id ?? formData?.logo_media_id ?? null;
    const footerMediaId =
        formData?.footer?.id ?? formData?.footer_id ?? formData?.footer_media_id ?? null;
    const faviconMediaId =
        formData?.fav_icon?.id ?? formData?.fav_icon_id ?? formData?.fav_icon_media_id ?? null;

    const handleUploadGeneric = (
        file,
        { setError, setUploadedName, fieldName, setIsUploading, previewKey }
    ) => {
        setError("");
        setUploadedName("");
        if (!file) return;

        const fd = new FormData();
        fd.append("file", file);
        setIsUploading(true);

        uploadMedia(fd, {
            onSuccess: (res) => {
                const fileName = res?.data?.file_name || res?.data?.name || "";
                if (!fileName) {
                    setError("Cannot get uploaded file name from server");
                    setIsUploading(false);
                    return;
                }

                setUploadedName(fileName);

                // ✅ خزّن اسم الملف فقط (أساسي للإرسال)
                setFormData((prev) => ({
                    ...prev,
                    [fieldName]: fileName,
                    // previewKey اختياري: لو عندك preview من السيرفر/أو بتبني url هنا لاحقًا
                    ...(previewKey ? { [previewKey]: prev?.[previewKey] } : {}),
                }));

                setIsUploading(false);
            },
            onError: () => {
                setError("Failed to upload image, please try again");
                setIsUploading(false);
            },
        });
    };

    // ✅ حذف: امسح key نهائيًا من formData + صفّر الـ UI states الخارجية
    const handleDeleteGeneric = ({
        setError,
        setUploadedName,
        setIsUploading,
        fieldName,
        previewKey,
        extraKeys = [],
    }) => {
        setError("");
        setUploadedName("");
        setIsUploading(false);

        setFormData((prev) => {
            const next = { ...prev };

            // أهم حاجة: حذف key الاسم عشان مايتبعتش أصلاً
            delete next[fieldName];

            // مفاتيح preview/objects لو موجودة
            if (previewKey) delete next[previewKey];
            extraKeys.forEach((k) => delete next[k]);

            return next;
        });
    };

    const handleLogoChange = (e) => {
        handleUploadGeneric(e.target.files?.[0], {
            setError: setLogoError,
            setUploadedName: setLogoUploadedName,
            fieldName: "logo_name",
            setIsUploading: setIsUploadingLogo,
            previewKey: "logo_preview",
        });
    };

    const handleFooterChange = (e) => {
        handleUploadGeneric(e.target.files?.[0], {
            setError: setFooterError,
            setUploadedName: setFooterUploadedName,
            fieldName: "footer_name",
            setIsUploading: setIsUploadingFooter,
            previewKey: "footer_preview",
        });
    };

    const handleFaviconChange = (e) => {
        handleUploadGeneric(e.target.files?.[0], {
            setError: setFaviconError,
            setUploadedName: setFaviconUploadedName,
            fieldName: "fav_icon_name",
            setIsUploading: setIsUploadingFavicon,
            previewKey: "fav_icon_preview",
        });
    };

    return (
        <Accordion
            expanded={expanded === "branding"}
            onChange={handleAccordionChange("branding")}
            disableGutters
            elevation={0}
            square={false}
            sx={accordionBaseSx}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#93C5FD" : "#1D4ED8" }} />}
                sx={summarySx}
            >
                <Typography fontWeight={600} sx={{ color: isDark ? "#E5E7EB" : "#0F172A", fontSize: 15 }}>
                    Branding
                </Typography>
            </AccordionSummary>

            <AccordionDetails sx={detailsSx}>
                <Typography variant="caption" color="text.secondary">
                    Upload or manage company logo, footer image and favicon.
                </Typography>

                <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
                    {/* Logo */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3, flexDirection: "column", alignItems: "center" }}>
                        <FormInput
                            required={false}
                            type="file"
                            name="logo"
                            label="Logo"
                            labelImage="Choose logo"
                            labelBioImage={
                                logoUploadedName ? `Current: ${logoUploadedName}` : "Drag & drop or click to upload"
                            }
                            value={formData?.logo_preview || formData?.logo?.url || ""}
                            onChange={handleLogoChange}
                            isUploading={isUploadingLogo}
                            errorMessage={logoError}
                            successMessage={logoUploadedName && !logoError ? "Logo uploaded successfully" : ""}
                            inputProps={{ accept: "image/*" }}

                            // ✅ يفعّل الحذف من السيرفر داخل InputFileUpload لو موجود id
                            mediaId={logoMediaId}

                            // ✅ بعد الحذف: امسح keys من formData عشان مايتبعتش logo_name
                            onDeleted={() =>
                                handleDeleteGeneric({
                                    setError: setLogoError,
                                    setUploadedName: setLogoUploadedName,
                                    setIsUploading: setIsUploadingLogo,
                                    fieldName: "logo_name",
                                    previewKey: "logo_preview",
                                    extraKeys: ["logo", "logo_id", "logo_media_id"],
                                })
                            }
                        />
                    </Box>

                    {/* Footer */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3, flexDirection: "column", alignItems: "center" }}>
                        <FormInput
                            required={false}
                            type="file"
                            name="footer"
                            label="Footer Image"
                            labelImage="Choose footer image"
                            labelBioImage={
                                footerUploadedName ? `Current: ${footerUploadedName}` : "Drag & drop or click to upload"
                            }
                            value={formData?.footer_preview || formData?.footer?.url || ""}
                            onChange={handleFooterChange}
                            isUploading={isUploadingFooter}
                            errorMessage={footerError}
                            successMessage={footerUploadedName && !footerError ? "Footer image uploaded successfully" : ""}
                            inputProps={{ accept: "image/*" }}
                            mediaId={footerMediaId}
                            onDeleted={() =>
                                handleDeleteGeneric({
                                    setError: setFooterError,
                                    setUploadedName: setFooterUploadedName,
                                    setIsUploading: setIsUploadingFooter,
                                    fieldName: "footer_name",
                                    previewKey: "footer_preview",
                                    extraKeys: ["footer", "footer_id", "footer_media_id"],
                                })
                            }
                        />
                    </Box>

                    {/* Favicon */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3, flexDirection: "column", alignItems: "center" }}>
                        <FormInput
                            required={false}
                            type="file"
                            name="fav_icon"
                            label="Favicon"
                            labelImage="Choose favicon"
                            labelBioImage={
                                faviconUploadedName ? `Current: ${faviconUploadedName}` : "Drag & drop or click to upload"
                            }
                            value={formData?.fav_icon_preview || formData?.fav_icon?.url || ""}
                            onChange={handleFaviconChange}
                            isUploading={isUploadingFavicon}
                            errorMessage={faviconError}
                            successMessage={faviconUploadedName && !faviconError ? "Favicon uploaded successfully" : ""}
                            inputProps={{ accept: "image/*" }}
                            mediaId={faviconMediaId}
                            onDeleted={() =>
                                handleDeleteGeneric({
                                    setError: setFaviconError,
                                    setUploadedName: setFaviconUploadedName,
                                    setIsUploading: setIsUploadingFavicon,
                                    fieldName: "fav_icon_name",
                                    previewKey: "fav_icon_preview",
                                    extraKeys: ["fav_icon", "fav_icon_id", "fav_icon_media_id"],
                                })
                            }
                        />
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default BrandingSection;
